'use client';

import React, { useEffect, useState, useContext } from 'react';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import {
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  CheckCircleIcon,
  ShieldExclamationIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

const ProgressBar = ({ progress }) => (
  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
    <div
      className="bg-green-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
      style={{ width: `${progress}%` }}
    ></div>
  </div>
);

export default function OnStationModal({
  isOpen,
  onClose,
  selectedMeasurements,
  onSubmit,
  onCloseWithReset,
}) {
  const { user, session } = useContext(AuthContext);
  
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const [validationResults, setValidationResults] = useState([]);
  const [unresolvedMeasurements, setUnresolvedMeasurements] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [projectSummary, setProjectSummary] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [measurementsPerPage] = useState(5);
  const [selectAllProject, setSelectAllProject] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    
    let isCancelled = false;

    const processValidationQueue = async () => {
      setIsValidating(true);
      setError(null);
      setSubmissionSuccess(false);
      setValidationResults([]);
      setUnresolvedMeasurements([]);
      setSelectedProjects({});
      setProjectSummary([]);
      
      let orgId;
      try {
        const { data: orgData, error: orgError } = await supabase.from('organization_members').select('organizations(id, name)').eq('user_id', user.id).single();
        if (orgError || !orgData?.organizations?.id) throw new Error('Could not fetch organization.');
        orgId = orgData.organizations.id;
      } catch (err) {
        if (isCancelled) return;
        setError(err.message);
        setIsValidating(false);
        return;
      }

      for (const measurement of selectedMeasurements) {
        if (isCancelled) return;
        try {
          if (!session) throw new Error('Authentication session not found.');

          const response = await fetch(`https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/validate-onstation-projects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
            body: JSON.stringify({
              user_id: user.id,
              organization_id: orgId,
              measurements: [{ id: measurement.id, GpsCoordinates: measurement.GpsCoordinates }],
            }),
          });
          
          if (isCancelled) return;
          if (!response.ok) throw new Error(`Network error: ${response.statusText}`);
          
          const result = await response.json();
          const singleResult = result.results[0];

          if (singleResult?.status === 'error' && singleResult.message?.includes('Invalid API key')) {
            throw new Error('invalid_api_key');
          }
          if (isCancelled) return;

          setValidationResults(prev => [...prev, singleResult]);

          if (singleResult.status === 'single_project' && singleResult.projects.length === 1) {
            setSelectedProjects(prev => ({ ...prev, [singleResult.measurement_id]: singleResult.projects[0].code }));
          } else if (singleResult.status === 'multiple_projects') {
            setUnresolvedMeasurements(prev => [...prev, singleResult]);
          }
          await new Promise(res => setTimeout(res, 50));
        } catch (err) {
          if (isCancelled) return;
          if (err.message === 'invalid_api_key') {
            setError('api_key_invalid');
          } else {
            setError(err.message || 'A validation error occurred.');
          }
          setIsValidating(false);
          return;
        }
      }
      if (isCancelled) return;
      setIsValidating(false);
    };
    processValidationQueue();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, user, selectedMeasurements, session]);
  
  useEffect(() => {
    if (submissionSuccess) {
      const timer = setTimeout(() => { onCloseWithReset(); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submissionSuccess, onCloseWithReset]);

  useEffect(() => {
    const projectCounts = {};
    const projectDetails = {};
    Object.keys(selectedProjects).forEach(measurementId => {
        const projectCode = selectedProjects[measurementId];
        projectCounts[projectCode] = (projectCounts[projectCode] || 0) + 1;
        const measurementResult = validationResults.find(r => r.measurement_id === measurementId);
        if (!projectDetails[projectCode] && measurementResult) {
            const project = measurementResult.projects.find(p => p.code === projectCode);
            if (project) {
                projectDetails[projectCode] = { name: project.name, state: project.state };
            }
        }
    });
    const summary = Object.keys(projectCounts).map(code => ({ code, name: projectDetails[code]?.name || 'Unknown', state: projectDetails[code]?.state || 'N/A', count: projectCounts[code] }));
    setProjectSummary(summary);
  }, [selectedProjects, validationResults]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const orgResponse = await supabase.from('organization_members').select('organization_id').eq('user_id', user.id).single();
      if (orgResponse.error || !orgResponse.data.organization_id) throw new Error("Could not verify user's organization.");
      const organizationId = orgResponse.data.organization_id;

      const measurementsToSubmit = selectedMeasurements.filter((m) => selectedProjects[m.id]).map((m) => ({ id: m.id, GpsCoordinates: m.GpsCoordinates, LocalTimeOfMeasurement: m.LocalTimeOfMeasurement, Density: m.Density, Compaction: m.Compaction, SurfaceTemp: m.SurfaceTemp, projectCode: selectedProjects[m.id] }));
      if (measurementsToSubmit.length === 0) throw new Error('No measurements are selected for submission.');

      const response = await fetch(`https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/submit_onstation_measurements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ user_id: user.id, organization_id: organizationId, measurements: measurementsToSubmit }),
      });
      if (!response.ok) { const errorText = await response.text(); throw new Error(`Failed to submit measurements: ${errorText}`); }
      
      const result = await response.json();
      setSubmissionSuccess(true);
      if (onSubmit) onSubmit(measurementsToSubmit);

      await fetch(`https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/process_onstation_measurements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ export_batch_id: result.export_batch_id }),
      });
    } catch (err) {
      setError(err.message || 'Failed to send measurements to OnStation.');
      setSubmissionSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectProject = (measurementId, projectCode) => {
    setSelectedProjects((prev) => ({ ...prev, [measurementId]: projectCode }));
  };

  const handleSelectAllProject = (projectCode) => {
    setSelectAllProject(projectCode);
    const newSelectedProjects = { ...selectedProjects };
    unresolvedMeasurements.forEach((m) => {
      if (m.status === 'multiple_projects') {
        newSelectedProjects[m.measurement_id] = projectCode;
      }
    });
    setSelectedProjects(newSelectedProjects);
  };

  const indexOfLastMeasurement = currentPage * measurementsPerPage;
  const indexOfFirstMeasurement = indexOfLastMeasurement - measurementsPerPage;
  const currentUnresolved = unresolvedMeasurements.slice(indexOfFirstMeasurement, indexOfLastMeasurement);
  const totalPages = Math.ceil(unresolvedMeasurements.length / measurementsPerPage);
  
  const totalAssignedCount = Object.keys(selectedProjects).length;
  const progress = selectedMeasurements.length > 0 ? (validationResults.length / selectedMeasurements.length) * 100 : 0;
  
  const isValidationComplete = !isValidating && !error;
  const noMatchesFound = isValidationComplete && totalAssignedCount === 0 && unresolvedMeasurements.length === 0 && validationResults.length > 0;
  const readyToSubmit = isValidationComplete && totalAssignedCount > 0 && unresolvedMeasurements.length === 0;

  if (!isOpen) return null;

  const renderBodyContent = () => {
    if (error || submissionSuccess) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-4">
          {error === 'api_key_invalid' && <>
            <ShieldExclamationIcon className="h-12 w-12 text-red-500" /><h3 className="mt-4 text-lg font-semibold text-gray-800">Connection Failed</h3><p className="mt-2 max-w-sm text-sm text-gray-600">The API key for your OnStation integration is invalid or has expired.</p><p className="mt-4 text-sm text-gray-500 bg-gray-100 p-3 rounded-md border">Please contact an administrator to update it in your organization's settings.</p>
          </>}
          {error && error !== 'api_key_invalid' && <>
            <ExclamationTriangleIcon className="h-12 w-12 text-amber-500" /><h3 className="mt-4 text-lg font-semibold text-gray-800">An Error Occurred</h3><p className="mt-2 max-w-md text-sm text-gray-600">{error}</p>
          </>}
          {submissionSuccess && <>
            <CheckCircleIcon className="h-12 w-12 text-green-500" /><h3 className="mt-4 text-lg font-semibold text-gray-800">Submission Received!</h3><p className="mt-2 text-sm text-gray-600">Your measurements are being processed.</p>
          </>}
        </div>
      );
    }
    
    return (
      <>
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-3">Project Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg border"><div className="flex justify-between items-center"><div><p className="text-sm font-medium text-gray-600">Total Measurements</p><p className="text-2xl font-bold text-gray-800">{selectedMeasurements.length}</p></div><MapPinIcon className="h-8 w-8 text-gray-400" /></div></div>
            <div className="bg-gray-50 p-4 rounded-lg border"><div className="flex justify-between items-center"><div><p className="text-sm font-medium text-gray-600">Assigned to Projects</p><p className="text-2xl font-bold text-gray-800">{totalAssignedCount}</p></div><CheckCircleIcon className="h-8 w-8 text-green-500" /></div></div>
          </div>
        </div>

        {isValidating && (
          <div className="pt-6 border-t">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-base font-semibold text-gray-800">Validating...</h3>
              <p className="text-sm text-gray-600">{validationResults.length} of {selectedMeasurements.length}</p>
            </div>
            <ProgressBar progress={progress} />
          </div>
        )}

        {!isValidating && (
          <div className="pt-6 border-t">
            {noMatchesFound && (
              <div className="text-center px-4 py-8">
                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-amber-500" /><h3 className="mt-4 text-lg font-semibold text-gray-800">No Matching Projects Found</h3><p className="mt-2 max-w-md mx-auto text-sm text-gray-600">None of the selected measurements are located within an available OnStation project geofence.</p>
              </div>
            )}
            {unresolvedMeasurements.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">Action Required</h3><p className="text-sm text-gray-600 mb-4">Please assign the following measurements to a project.</p><div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Apply to all unassigned:</label><select value={selectAllProject} onChange={(e) => handleSelectAllProject(e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"><option value="">Select a project</option>{[...new Set(unresolvedMeasurements.flatMap((m) => m.projects.map((p) => p.code)))].map((code) => {const project = unresolvedMeasurements.find((m) => m.projects.some(p => p.code === code))?.projects.find((p) => p.code === code);return (<option key={code} value={code}>{project.name} ({project.state})</option>);})}</select></div><table className="min-w-full border-collapse"><thead><tr className="bg-gray-50"><th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-600">Description</th><th className="border-b px-4 py-2 text-left text-sm font-medium text-gray-600">Project</th></tr></thead><tbody>{currentUnresolved.map((r) => {const measurement = selectedMeasurements.find((m) => m.id === r.measurement_id);return (<tr key={r.measurement_id} className="border-b hover:bg-gray-50"><td className="px-4 py-2 text-sm text-gray-700">{measurement?.Description1 || 'N/A'}</td><td className="px-4 py-2 text-sm text-gray-700"><select value={selectedProjects[r.measurement_id] || ''} onChange={(e) => handleSelectProject(r.measurement_id, e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" disabled={!!selectAllProject}><option value="">Select a project</option>{r.projects.map((p) => (<option key={p.code} value={p.code}>{p.name} ({p.state})</option>))}</select></td></tr>);})}</tbody></table>{totalPages > 1 && <nav className="flex justify-between items-center mt-4 text-sm"><button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-3 py-1 bg-white border rounded-md disabled:opacity-50">Previous</button><span>Page {currentPage} of {totalPages}</span><button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="px-3 py-1 bg-white border rounded-md disabled:opacity-50">Next</button></nav>}
              </div>
            )}
            {readyToSubmit && (
              <div>
                <div className="text-center">
                  <CheckCircleIcon className="mx-auto h-10 w-10 text-green-500" />
                  <h3 className="mt-2 text-lg font-semibold text-gray-800">Ready to Submit</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    You are about to send {totalAssignedCount} measurement{totalAssignedCount !== 1 ? 's' : ''} to the following project(s):
                  </p>
                </div>
                <div className="mt-6 space-y-3">
                  {projectSummary.map(project => (
                    <div key={project.code} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-800">{project.name}</p>
                          <p className="text-sm text-gray-500">{project.state}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-lg font-bold text-green-600">{project.count}</p>
                            <p className="text-xs text-gray-500">Measurement{project.count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  };
  
  const showFooter = !submissionSuccess;
  const showCloseOnlyFooter = error || (!isValidating && noMatchesFound);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[650px] flex flex-col border border-gray-200 overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800" id="modal-title">Send to OnStation</h2>
          <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"><XMarkIcon className="h-6 w-6" /></button>
        </div>
        <div className="flex-1 p-6 overflow-y-auto">{renderBodyContent()}</div>
        {showFooter && (
          <div className="flex-shrink-0 flex items-center justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
            {showCloseOnlyFooter ? (
              <button onClick={onCloseWithReset} className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700">Close</button>
            ) : (
              <>
                <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button onClick={handleSubmit} disabled={isSubmitting || isValidating || totalAssignedCount === 0 || unresolvedMeasurements.some(m => !selectedProjects[m.measurement_id])} className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center">
                  {isSubmitting ? (<><ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />Submitting...</>) : (`Submit ${totalAssignedCount} Measurement${totalAssignedCount !== 1 ? 's' : ''}`)}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}